/** Exceptions (Demo Mode only) - single consolidated hub for supplier exceptions and catalogue
 * exceptions, per the Stage 5 Procurement spec. Replaces having to check the standalone
 * Supplier Exceptions screen and Catalogue Governance tabs separately - "Blocks go-live" is the
 * key field, so this screen can separate general data-quality issues from true launch blockers.
 * All figures are static mock data - see src/mocks/data/procurementExceptionsData.ts. */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Accordion, AccordionDetails, AccordionSummary, Box, Button, Chip, Grid, MenuItem, Stack, SvgIcon, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, useTheme,
} from '@mui/material';
import {
  Block, ExpandLess as ExpandLessIcon, ExpandMore as ExpandMoreIcon, ReportProblem, RuleOutlined, WarningAmber,
} from '@mui/icons-material';
import PageArea from '../../../components/shared/PageArea';
import PageTitle from '../../../components/shared/PageTitle';
import { Section } from '../../../components/ui/Section';
import { TableHeader } from '../../../components/forms/TableHeader';
import { Pill } from '../../../components/shared/Pill';
import {
  EXCEPTION_SEVERITY_COLOR, EXCEPTION_STATUS_COLOR, ExceptionSeverity, ExceptionStatus, ExceptionType,
  ProcurementException, procurementExceptions,
} from '../../../mocks/data/procurementExceptionsData';

const ALL = 'All';
const TILE_COLORS = ['#d32f2f', '#ed6c02', '#1976d2', '#9c27b0'];

const ExceptionsPage = () => {
  const { t } = useTranslation('procurementExceptions');
  const navigate = useNavigate();
  const theme = useTheme();
  const [exceptions, setExceptions] = useState<ProcurementException[]>(procurementExceptions);
  const [typeFilter, setTypeFilter] = useState<ExceptionType | typeof ALL>(ALL);
  const [severityFilter, setSeverityFilter] = useState<ExceptionSeverity | typeof ALL>(ALL);
  const [statusFilter, setStatusFilter] = useState<ExceptionStatus | typeof ALL>(ALL);
  const [isAdvancedOpen, setIsAdvancedOpen] = useState<boolean>(false);

  const rows = useMemo(
    () => exceptions.filter((exception) => (typeFilter === ALL || exception.type === typeFilter)
      && (severityFilter === ALL || exception.severity === severityFilter)
      && (statusFilter === ALL || exception.status === statusFilter)),
    [exceptions, typeFilter, severityFilter, statusFilter],
  );

  const openCount = exceptions.filter((e) => e.status === 'Open' || e.status === 'InProgress').length;
  const blockingCount = exceptions.filter((e) => e.blocksGoLive && e.status !== 'Resolved' && e.status !== 'Waived').length;
  const supplierCount = exceptions.filter((e) => e.type === 'Supplier').length;
  const catalogueCount = exceptions.filter((e) => e.type === 'Catalogue').length;

  const TILES = [
    { key: 'blocking', label: t('tileBlockingGoLive', 'Blocking go-live'), value: blockingCount, subtitle: t('tileBlockingGoLiveSubtitle', 'Critical launch blockers'), icon: <Block /> },
    { key: 'open', label: t('tileOpenExceptions', 'Open exceptions'), value: openCount, subtitle: t('tileOpenExceptionsSubtitle', 'Open or in progress'), icon: <ReportProblem /> },
    { key: 'supplier', label: t('tileSupplierExceptions', 'Supplier exceptions'), value: supplierCount, subtitle: t('tileSupplierExceptionsSubtitle', 'Supplier-related issues'), icon: <WarningAmber /> },
    { key: 'catalogue', label: t('tileCatalogueExceptions', 'Catalogue exceptions'), value: catalogueCount, subtitle: t('tileCatalogueExceptionsSubtitle', 'Catalogue-related issues'), icon: <RuleOutlined /> },
  ];

  const updateStatus = (id: number, status: ExceptionStatus) => {
    setExceptions((current) => current.map((exception) => (exception.id === id ? { ...exception, status } : exception)));
  };

  const handleOpen = (exception: ProcurementException) => {
    if (exception.type === 'Supplier') {
      navigate('/procurement/suppliers');
    } else {
      navigate('/procurement/catalogues');
    }
  };

  return (
    <PageArea>
      <PageTitle subTitle={t('pageSubtitle', 'Consolidated supplier and catalogue exceptions - the single place to see what is blocking a site from going live')}>
        {t('pageTitle', 'Exceptions')}
      </PageTitle>

      <Grid container
        spacing={2}
        sx={{ mt: 2, mb: 4 }}>
        {TILES.map((tile, index) => (
          <Grid item
            key={tile.key}
            xs={6}
            md={3}>
            <Section style={{ p: 4, mb: 0 }}>
              <Box display="flex"
                alignItems="center"
                justifyContent="space-between">
                <Box flex={1}>
                  <Box sx={{ color: theme.palette.custom.gray[600], fontSize: '10px', textTransform: 'uppercase', mb: 0.25, fontWeight: 500 }}>
                    {tile.label}
                  </Box>
                  <Box sx={{ fontSize: '20px', fontWeight: 600, color: theme.palette.custom.gray[900], mb: 0.25 }}>
                    {tile.value}
                  </Box>
                  <Box sx={{ fontSize: '12px', color: theme.palette.custom.gray[600] }}>
                    {tile.subtitle}
                  </Box>
                </Box>
                <Box sx={{
                  backgroundColor: `${TILE_COLORS[index % TILE_COLORS.length]}20`,
                  borderRadius: '50%',
                  width: 48,
                  height: 48,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: TILE_COLORS[index % TILE_COLORS.length],
                }}>
                  <SvgIcon fontSize="medium">{tile.icon}</SvgIcon>
                </Box>
              </Box>
            </Section>
          </Grid>
        ))}
      </Grid>

      <Stack direction="row"
        spacing={1}
        alignItems="center"
        sx={{ mb: 3, flexWrap: 'wrap', rowGap: 1 }}>
        {(['Supplier', 'Catalogue'] as ExceptionType[]).map((type) => (
          <Chip key={type}
            label={type === 'Supplier' ? t('typeSupplier', 'Supplier') : t('typeCatalogue', 'Catalogue')}
            color={typeFilter === type ? 'primary' : 'default'}
            onClick={() => setTypeFilter((prev) => (prev === type ? ALL : type))}
          />
        ))}
        <Box sx={{ width: '1px', height: 24, backgroundColor: '#E0E0E0' }} />
        {(['Critical', 'High', 'Medium', 'Low'] as ExceptionSeverity[]).map((severity) => (
          <Chip key={severity}
            label={t(`severity${severity}`, severity)}
            color={severityFilter === severity ? 'primary' : 'default'}
            onClick={() => setSeverityFilter((prev) => (prev === severity ? ALL : severity))}
          />
        ))}
      </Stack>

      <Accordion
        variant="borderless"
        expanded={isAdvancedOpen}
        onChange={(_, expanded) => setIsAdvancedOpen(expanded)}
      >
        <AccordionSummary>
          <Grid container
            alignItems="center"
            justifyContent="space-between"
            sx={{ width: '100%' }}>
            <Grid item>
              <Grid container
                spacing={1}
                alignItems="center">
                <Grid item>{t('advancedFilters', 'Advanced filters')}</Grid>
                <Grid item
                  style={{ marginTop: '8px' }}>
                  {isAdvancedOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <Button
                size="md"
                variant="secondary"
                onClick={() => {
                  setTypeFilter(ALL);
                  setSeverityFilter(ALL);
                  setStatusFilter(ALL);
                }}>
                {t('clearFilters', 'Clear filters')}
              </Button>
            </Grid>
          </Grid>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container
            spacing={3}>
            <Grid item
              lg={3}
              sm={6}
              xs={12}>
              <TextField select
                label={t('filterType', 'Type')}
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value as ExceptionType | typeof ALL)}
                fullWidth
                size="small"
              >
                <MenuItem value={ALL}>{t('filterAllTypes', 'All types')}</MenuItem>
                <MenuItem value="Supplier">{t('typeSupplier', 'Supplier')}</MenuItem>
                <MenuItem value="Catalogue">{t('typeCatalogue', 'Catalogue')}</MenuItem>
              </TextField>
            </Grid>
            <Grid item
              lg={3}
              sm={6}
              xs={12}>
              <TextField select
                label={t('filterSeverity', 'Severity')}
                value={severityFilter}
                onChange={(e) => setSeverityFilter(e.target.value as ExceptionSeverity | typeof ALL)}
                fullWidth
                size="small"
              >
                <MenuItem value={ALL}>{t('filterAllSeverities', 'All severities')}</MenuItem>
                {(['Critical', 'High', 'Medium', 'Low'] as ExceptionSeverity[]).map((severity) => (
                  <MenuItem key={severity}
                    value={severity}>{t(`severity${severity}`, severity)}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item
              lg={3}
              sm={6}
              xs={12}>
              <TextField select
                label={t('filterStatus', 'Status')}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as ExceptionStatus | typeof ALL)}
                fullWidth
                size="small"
              >
                <MenuItem value={ALL}>{t('filterAllStatuses', 'All statuses')}</MenuItem>
                <MenuItem value="Open">{t('statusOpen', 'Open')}</MenuItem>
                <MenuItem value="InProgress">{t('statusInProgress', 'In progress')}</MenuItem>
                <MenuItem value="Waived">{t('statusWaived', 'Waived')}</MenuItem>
                <MenuItem value="Resolved">{t('statusResolved', 'Resolved')}</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      <Section>
        <TableHeader>
          {t('tableTitle', 'Exceptions')}
          <Pill text={t('exceptionCount', '{{count}} exceptions', { count: rows.length })} />
        </TableHeader>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('columnType', 'Type')}</TableCell>
                <TableCell>{t('columnException', 'Exception')}</TableCell>
                <TableCell>{t('columnSeverity', 'Severity')}</TableCell>
                <TableCell>{t('columnSupplier', 'Supplier')}</TableCell>
                <TableCell>{t('columnCatalogue', 'Catalogue')}</TableCell>
                <TableCell>{t('columnSite', 'Site')}</TableCell>
                <TableCell>{t('columnOwner', 'Owner')}</TableCell>
                <TableCell>{t('columnDueDate', 'Due date')}</TableCell>
                <TableCell>{t('columnStatus', 'Status')}</TableCell>
                <TableCell>{t('columnBlocksGoLive', 'Blocks go-live')}</TableCell>
                <TableCell align="right">{t('columnActions', 'Actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={11}>{t('noExceptionsMatch', 'No exceptions match the selected filters.')}</TableCell></TableRow>
              )}
              {rows.map((exception) => (
                <TableRow key={exception.id}>
                  <TableCell><Chip label={exception.type === 'Supplier' ? t('typeSupplier', 'Supplier') : t('typeCatalogue', 'Catalogue')}
                    size="small"
                    variant="outlined" /></TableCell>
                  <TableCell>{exception.exceptionType}</TableCell>
                  <TableCell><Chip label={t(`severity${exception.severity}`, exception.severity)}
                    size="small"
                    color={EXCEPTION_SEVERITY_COLOR[exception.severity]} /></TableCell>
                  <TableCell>{exception.supplier ?? '-'}</TableCell>
                  <TableCell>{exception.catalogue ?? '-'}</TableCell>
                  <TableCell>{exception.site}</TableCell>
                  <TableCell>{exception.owner ?? '-'}</TableCell>
                  <TableCell>{exception.dueDate ?? '-'}</TableCell>
                  <TableCell><Chip label={t(`status${exception.status}`, exception.status)}
                    size="small"
                    color={EXCEPTION_STATUS_COLOR[exception.status]} /></TableCell>
                  <TableCell>
                    {exception.blocksGoLive ? (
                      <Chip label={t('blocksGoLiveYes', 'Yes')}
                        size="small"
                        color="error" />
                    ) : (
                      <Chip label={t('blocksGoLiveNo', 'No')}
                        size="small"
                        variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row"
                      spacing={1}
                      justifyContent="flex-end">
                      <Button size="md"
                        variant="secondary"
                        onClick={() => handleOpen(exception)}>
                        {t('actionOpen', 'Open')}
                      </Button>
                      {exception.status !== 'Waived' && exception.status !== 'Resolved' && (
                        <Button size="md"
                          variant="secondary"
                          onClick={() => updateStatus(exception.id, 'Waived')}>
                          {t('actionWaive', 'Waive')}
                        </Button>
                      )}
                      {exception.status !== 'Resolved' && (
                        <Button size="md"
                          variant="primary"
                          onClick={() => updateStatus(exception.id, 'Resolved')}>
                          {t('actionResolve', 'Resolve')}
                        </Button>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Section>
    </PageArea>
  );
};

export default ExceptionsPage;
